'use client';

import Script from 'next/script';

import { useWebGL } from '@/hooks/useWebGL';
import { DEFAULT_MAP_LOCATION_PAGE_ZOOM, MAP_STYLE } from '@/utils/constants';
import { hasValidCoordinates } from '@/utils/mapbox';

import { ImageWithPlaceholder } from './ImageWithPlaceholder';

const MAPBOX_SCRIPT_URL =
  'https://api.mapbox.com/mapbox-gl-js/v2.12.0/mapbox-gl.js';

type InteractiveMapProps = {
  mapboxAccessToken: string;
  mapboxStaticAccessToken: string;
  lat: number;
  lng: number;
  dataTestId?: string;
  className?: string;
};

export default function InteractiveMap({
  mapboxAccessToken,
  mapboxStaticAccessToken,
  lat,
  lng,
  dataTestId,
  className,
}: InteractiveMapProps) {
  const { isWebGLSupported } = useWebGL();
  const hasValidCoords = hasValidCoordinates({
    lat,
    lng,
  });

  const staticImageUrl = `https://api.mapbox.com/styles/v1/mapbox/${MAP_STYLE}/static/${lng},${lat},${DEFAULT_MAP_LOCATION_PAGE_ZOOM},0,0/800x200?access_token=${mapboxStaticAccessToken}`;

  const handleOnLoadMapboxScript = () => {
    window.mapboxgl.accessToken = mapboxAccessToken;
    const map = new window.mapboxgl.Map({
      container: 'map-location',
      style: `mapbox://styles/mapbox/${MAP_STYLE}`,
      center: [lng, lat],
      zoom: DEFAULT_MAP_LOCATION_PAGE_ZOOM,
    });

    map.addControl(
      new window.mapboxgl.NavigationControl({ showCompass: false }),
      'top-left'
    );

    const el = document.createElement('div');
    el.className = 'w-4 h-4 rounded-full bg-teal-5';
    el.ariaLabel = '';
    new window.mapboxgl.Marker(el).setLngLat([lng, lat]).addTo(map);
  };

  return (
    <>
      {!isWebGLSupported || !hasValidCoords ? (
        <ImageWithPlaceholder
          className={className}
          data-testid={dataTestId}
          src={staticImageUrl}
          alt=""
          height={200}
          width={800}
        />
      ) : (
        <>
          <Script
            src={MAPBOX_SCRIPT_URL}
            onReady={handleOnLoadMapboxScript}
            strategy="lazyOnload"
          />
          <div
            className={className}
            data-testid={dataTestId}
            id="map-location"
          />
        </>
      )}
    </>
  );
}
